import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayoutNew';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Pencil, Trash2, Home, Filter, CheckSquare, FileText, Phone, User } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUnits() {
  const { projectId: urlProjectId } = useParams();
  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [sitemapUnits, setSitemapUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(urlProjectId || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [sitemapEditOpen, setSitemapEditOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedSitemapUnit, setSelectedSitemapUnit] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedSitemapNums, setSelectedSitemapNums] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('available');
  const [sitemapFormData, setSitemapFormData] = useState({
    villa_number: '', villa_type: 'B', sqm: 203, status: 'available', buyer_name: '', phone: '', price: '', contract_number: '', position_x: '', position_y: ''
  });
  const [formData, setFormData] = useState({
    project_id: '',
    unit_number: '',
    status: 'available',
    price: '',
    design_image: '',
    view_image: '',
    position_x: 50,
    position_y: 50,
    bedrooms: 0,
    bathrooms: 0,
    area: '',
    floor: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();

  const isBakrajoHills = selectedProjectId === 'bakrajo-hills';

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, { headers: getAuthHeaders() });
      setProjects(response.data.filter(p => p.id === 'bakrajo-hills'));
      if (urlProjectId) {
        setSelectedProjectId(urlProjectId);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchUnits = useCallback(async () => {
    try {
      // Fetch regular units
      let url = `${API_URL}/api/units`;
      const params = new URLSearchParams();
      if (selectedProjectId && selectedProjectId !== 'bakrajo-hills') params.append('project_id', selectedProjectId);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, { headers: getAuthHeaders() });
      setUnits(selectedProjectId === 'bakrajo-hills' ? [] : response.data);
      
      // Fetch sitemap units for Bakrajo Hills
      if (!selectedProjectId || selectedProjectId === 'bakrajo-hills') {
        const smUrl = `${API_URL}/api/admin/sitemap-units/bakrajo-hills${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`;
        const smRes = await axios.get(smUrl, { headers: getAuthHeaders() });
        // Apply type and zone filter client-side
        let filtered = smRes.data;
        if (typeFilter !== 'all') filtered = filtered.filter(u => u.villa_type === typeFilter);
        if (zoneFilter !== 'all') filtered = filtered.filter(u => u.zone === zoneFilter);
        if (searchQuery.trim()) {
          const q = searchQuery.trim().toLowerCase();
          filtered = filtered.filter(u =>
            String(u.villa_number).includes(q) ||
            (u.buyer_name || '').toLowerCase().includes(q) ||
            (u.contract_number || '').toLowerCase().includes(q) ||
            (u.phone || '').toLowerCase().includes(q)
          );
        }
        // Sort ascending by villa_number
        filtered = [...filtered].sort((a, b) => (a.villa_number || 0) - (b.villa_number || 0));
        setSitemapUnits(filtered);
      } else {
        setSitemapUnits([]);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      toast.error('Failed to load units');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, statusFilter, typeFilter, zoneFilter, searchQuery, getAuthHeaders]);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  const handleOpenDialog = (unit = null) => {
    if (unit) {
      setSelectedUnit(unit);
      setFormData({
        project_id: unit.project_id,
        unit_number: unit.unit_number,
        status: unit.status,
        price: unit.price,
        design_image: unit.design_image || '',
        view_image: unit.view_image || '',
        position_x: unit.position_x,
        position_y: unit.position_y,
        bedrooms: unit.bedrooms || 0,
        bathrooms: unit.bathrooms || 0,
        area: unit.area || '',
        floor: unit.floor || '',
        description: unit.description || ''
      });
    } else {
      setSelectedUnit(null);
      setFormData({
        project_id: selectedProjectId || (projects.length > 0 ? projects[0].id : ''),
        unit_number: '',
        status: 'available',
        price: '',
        design_image: '',
        view_image: '',
        position_x: 50,
        position_y: 50,
        bedrooms: 0,
        bathrooms: 0,
        area: '',
        floor: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.project_id || !formData.unit_number || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (selectedUnit) {
        await axios.put(
          `${API_URL}/api/units/${selectedUnit.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Unit updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/units`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Unit created successfully');
      }
      setDialogOpen(false);
      fetchUnits();
    } catch (error) {
      console.error('Error saving unit:', error);
      toast.error('Failed to save unit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUnit) return;

    try {
      await axios.delete(
        `${API_URL}/api/units/${selectedUnit.id}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Unit deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUnit(null);
      fetchUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
      toast.error('Failed to delete unit');
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedUnits.length === 0 && selectedSitemapNums.length === 0) {
      toast.error('Please select at least one unit');
      return;
    }

    try {
      // Update regular units
      if (selectedUnits.length > 0) {
        await axios.post(
          `${API_URL}/api/units/bulk-status`,
          { unit_ids: selectedUnits, status: bulkStatus },
          { headers: getAuthHeaders() }
        );
      }
      // Update sitemap units
      if (selectedSitemapNums.length > 0) {
        await axios.post(
          `${API_URL}/api/admin/sitemap-units/bulk-status`,
          { villa_numbers: selectedSitemapNums, project_id: 'bakrajo-hills', status: bulkStatus },
          { headers: getAuthHeaders() }
        );
      }
      toast.success(`Updated ${selectedUnits.length + selectedSitemapNums.length} units to ${bulkStatus}`);
      setBulkDialogOpen(false);
      setSelectedUnits([]);
      setSelectedSitemapNums([]);
      fetchUnits();
    } catch (error) {
      console.error('Error updating units:', error);
      toast.error('Failed to update units');
    }
  };

  const handleOpenSitemapEdit = (unit) => {
    setSelectedSitemapUnit(unit);
    setSitemapFormData({
      villa_number: unit.villa_number,
      villa_type: unit.villa_type || 'B',
      sqm: unit.sqm || 203,
      status: unit.status || 'available',
      buyer_name: unit.buyer_name || '',
      phone: unit.phone || '',
      price: unit.price || '',
      contract_number: unit.contract_number || '',
      position_x: unit.position_x ?? '',
      position_y: unit.position_y ?? '',
    });
    setSitemapEditOpen(true);
  };

  const handleOpenSitemapAdd = () => {
    setSelectedSitemapUnit(null);
    setSitemapFormData({
      villa_number: '',
      villa_type: 'B',
      sqm: 203,
      status: 'available',
      buyer_name: '',
      phone: '',
      price: '',
      contract_number: '',
      position_x: '',
      position_y: '',
    });
    setSitemapEditOpen(true);
  };

  const handleSaveSitemapUnit = async () => {
    setSaving(true);
    try {
      if (selectedSitemapUnit) {
        // Update existing
        await axios.put(
          `${API_URL}/api/admin/sitemap-units/bakrajo-hills/${selectedSitemapUnit.villa_number}`,
          { ...sitemapFormData, villa_type: selectedSitemapUnit.villa_type },
          { headers: getAuthHeaders() }
        );
        toast.success(`Villa #${selectedSitemapUnit.villa_number} updated`);
      } else {
        // Create new
        if (!sitemapFormData.villa_number) {
          toast.error('Villa number is required');
          setSaving(false);
          return;
        }
        const payload = {
          villa_number: parseInt(sitemapFormData.villa_number, 10),
          villa_type: sitemapFormData.villa_type || 'B',
          sqm: parseInt(sitemapFormData.sqm, 10) || null,
          status: sitemapFormData.status,
          buyer_name: sitemapFormData.buyer_name,
          phone: sitemapFormData.phone,
          price: sitemapFormData.price,
          contract_number: sitemapFormData.contract_number,
          position_x: sitemapFormData.position_x === '' ? null : parseFloat(sitemapFormData.position_x),
          position_y: sitemapFormData.position_y === '' ? null : parseFloat(sitemapFormData.position_y),
        };
        await axios.post(
          `${API_URL}/api/admin/sitemap-units/bakrajo-hills`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success(`Villa #${payload.villa_number} (Type ${payload.villa_type}) created`);
      }
      setSitemapEditOpen(false);
      fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save unit');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSitemapUnit = async (unit) => {
    if (!window.confirm(`Delete Villa #${unit.villa_number} (Type ${unit.villa_type})? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/admin/sitemap-units/bakrajo-hills/${unit.villa_number}?villa_type=${unit.villa_type}`,
        { headers: getAuthHeaders() }
      );
      toast.success(`Villa #${unit.villa_number} deleted`);
      fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete unit');
    }
  };

  const toggleSitemapSelection = (num) => {
    setSelectedSitemapNums(prev =>
      prev.includes(num) ? prev.filter(n => n !== num) : [...prev, num]
    );
  };

  const toggleUnitSelection = (unitId) => {
    setSelectedUnits(prev =>
      prev.includes(unitId)
        ? prev.filter(id => id !== unitId)
        : [...prev, unitId]
    );
  };

  const toggleAllUnits = () => {
    if (selectedUnits.length === units.length) {
      setSelectedUnits([]);
    } else {
      setSelectedUnits(units.map(u => u.id));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <Badge className="status-available capitalize">Available</Badge>;
      case 'sold':
        return <Badge className="status-sold capitalize">Sold</Badge>;
      case 'pending':
        return <Badge className="status-pending capitalize">Pending</Badge>;
      case 'company_buyer':
        return <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40 capitalize">Company Buyer</Badge>;
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-units-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-white mb-2">Units</h1>
            <p className="text-white/60">Manage project units and their status</p>
          </div>
          <div className="flex items-center gap-2">
            {(selectedUnits.length > 0 || selectedSitemapNums.length > 0) && (
              <Button
                onClick={() => setBulkDialogOpen(true)}
                variant="outline"
                className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                data-testid="bulk-status-btn"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Update {selectedUnits.length + selectedSitemapNums.length} Selected
              </Button>
            )}
            <Button
              onClick={() => isBakrajoHills || !selectedProjectId ? handleOpenSitemapAdd() : handleOpenDialog()}
              className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-semibold rounded-sm px-6"
              data-testid="add-unit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Unit
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-white/50" />
                <span className="text-white/70 text-sm">Filters:</span>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1">
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="w-full sm:w-[200px] bg-white/5 border-white/10 text-white" data-testid="project-filter-select">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="all-projects">All Projects</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[150px] bg-white/5 border-white/10 text-white" data-testid="status-filter-select">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="company_buyer">Company Buyer</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
                {isBakrajoHills && (
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-full sm:w-[150px] bg-white/5 border-white/10 text-white" data-testid="type-filter-select">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="A">Type A (300m²)</SelectItem>
                      <SelectItem value="B">Type B (203m²)</SelectItem>
                      <SelectItem value="C">Type C (160m²)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {isBakrajoHills && (
                  <Select value={zoneFilter} onValueChange={setZoneFilter}>
                    <SelectTrigger className="w-full sm:w-[150px] bg-white/5 border-white/10 text-white" data-testid="zone-filter-select">
                      <SelectValue placeholder="All Zones" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="all">All Zones</SelectItem>
                      <SelectItem value="zone_1">Zone 1 (Active)</SelectItem>
                      <SelectItem value="zone_2">Zone 2 (Future)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <p className="text-white/50 text-sm">
                Showing <span className="text-white">{units.length + sitemapUnits.length}</span> units
              </p>
            </div>
            {/* Search bar */}
            <div className="mt-4">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by villa #, buyer name, contract # or phone…"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="units-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Units Table */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 bg-white/5 rounded animate-pulse" />
                ))}
              </div>
            ) : (units.length > 0 || sitemapUnits.length > 0) ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          (units.length > 0 && selectedUnits.length === units.length) ||
                          (sitemapUnits.length > 0 && selectedSitemapNums.length === sitemapUnits.length)
                        }
                        onCheckedChange={() => {
                          if (sitemapUnits.length > 0) {
                            setSelectedSitemapNums(
                              selectedSitemapNums.length === sitemapUnits.length ? [] : sitemapUnits.map(u => u.villa_number)
                            );
                          }
                          if (units.length > 0) {
                            toggleAllUnits();
                          }
                        }}
                        className="border-white/30"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="text-white/60">Villa #</TableHead>
                    <TableHead className="text-white/60">Type</TableHead>
                    <TableHead className="text-white/60">Price</TableHead>
                    <TableHead className="text-white/60">Buyer</TableHead>
                    <TableHead className="text-white/60">Phone</TableHead>
                    <TableHead className="text-white/60">Status</TableHead>
                    <TableHead className="text-white/60 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Sitemap units (Bakrajo Hills) */}
                  {sitemapUnits.map((unit) => (
                    <TableRow key={`sm-${unit.villa_type}-${unit.villa_number}`} className="border-white/10 hover:bg-white/5" data-testid={`sitemap-unit-row-${unit.villa_number}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedSitemapNums.includes(unit.villa_number)}
                          onCheckedChange={() => toggleSitemapSelection(unit.villa_number)}
                          className="border-white/30"
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-white font-medium">{unit.villa_number}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-white/70 text-sm">Type {unit.villa_type} - {unit.sqm}m²</span>
                      </TableCell>
                      <TableCell className="text-[#D4AF37] font-semibold">{unit.price || '-'}</TableCell>
                      <TableCell>
                        <span className="text-white/70 text-sm">{unit.buyer_name || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-white/50 text-sm">{unit.phone || '-'}</span>
                      </TableCell>
                      <TableCell>{getStatusBadge(unit.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenSitemapEdit(unit)}
                            className="text-white/70 hover:text-white hover:bg-white/10"
                            data-testid={`edit-sitemap-${unit.villa_number}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSitemapUnit(unit)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-sitemap-${unit.villa_number}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Regular units (other projects) */}
                  {units.map((unit) => {
                    const project = projects.find(p => p.id === unit.project_id);
                    return (
                      <TableRow key={unit.id} className="border-white/10 hover:bg-white/5" data-testid={`unit-row-${unit.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedUnits.includes(unit.id)}
                            onCheckedChange={() => toggleUnitSelection(unit.id)}
                            className="border-white/30"
                          />
                        </TableCell>
                        <TableCell>
                          <span className="text-white font-medium">{unit.unit_number}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-white/70 text-sm">{project?.name || 'Unknown'}</span>
                        </TableCell>
                        <TableCell className="text-[#D4AF37] font-semibold">{unit.price}</TableCell>
                        <TableCell>
                          <div className="text-white/70 text-sm">
                            {unit.bedrooms > 0 && <span>{unit.bedrooms} bed</span>}
                            {unit.bathrooms > 0 && <span> • {unit.bathrooms} bath</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-white/50 text-xs">
                            x:{unit.position_x}% y:{unit.position_y}%
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(unit.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(unit)} className="text-white/70 hover:text-white hover:bg-white/10" data-testid={`edit-unit-${unit.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedUnit(unit); setDeleteDialogOpen(true); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-unit-${unit.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="p-12 text-center">
                <Home className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50 mb-4">No units found</p>
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A059]"
                >
                  Add Your First Unit
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Unit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="unit-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl text-white">
              {selectedUnit ? 'Edit Unit' : 'Add New Unit'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Project *</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="unit-project-select">
                    <SelectValue placeholder="Select Project" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Unit Number *</Label>
                <Input
                  value={formData.unit_number}
                  onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                  placeholder="e.g., A-101"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-number-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Price *</Label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="e.g., $125,000"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="unit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="company_buyer">Company Buyer</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Bedrooms</Label>
                <Input
                  type="number"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-bedrooms-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Bathrooms</Label>
                <Input
                  type="number"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-bathrooms-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Area</Label>
                <Input
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  placeholder="e.g., 120 sqm"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-area-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Floor</Label>
                <Input
                  value={formData.floor}
                  onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                  placeholder="e.g., 1st Floor"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-floor-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Position X (% from left)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.position_x}
                  onChange={(e) => setFormData({ ...formData, position_x: parseFloat(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-position-x-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Position Y (% from top)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.position_y}
                  onChange={(e) => setFormData({ ...formData, position_y: parseFloat(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-position-y-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Design Image URL</Label>
                <Input
                  value={formData.design_image}
                  onChange={(e) => setFormData({ ...formData, design_image: e.target.value })}
                  placeholder="https://example.com/design.jpg"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-design-image-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">View Image URL</Label>
                <Input
                  value={formData.view_image}
                  onChange={(e) => setFormData({ ...formData, view_image: e.target.value })}
                  placeholder="https://example.com/view.jpg"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="unit-view-image-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Unit description..."
                className="bg-white/5 border-white/10 text-white min-h-[80px]"
                data-testid="unit-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#D4AF37] text-black hover:bg-[#C5A059]"
              data-testid="save-unit-btn"
            >
              {saving ? 'Saving...' : selectedUnit ? 'Update Unit' : 'Create Unit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10" data-testid="delete-unit-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Unit</DialogTitle>
          </DialogHeader>
          <p className="text-white/70">
            Are you sure you want to delete unit <span className="text-white font-semibold">{selectedUnit?.unit_number}</span>?
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-white/20 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-unit-btn"
            >
              Delete Unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10" data-testid="bulk-status-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Bulk Status Update</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-white/70 mb-4">
              Update status for <span className="text-white font-semibold">{selectedUnits.length + selectedSitemapNums.length}</span> selected units
            </p>
            <div className="space-y-2">
              <Label className="text-white/70">New Status</Label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="bulk-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="company_buyer">Company Buyer</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} className="border-white/20 text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={handleBulkStatusUpdate} className="bg-[#D4AF37] text-black hover:bg-[#C5A059]" data-testid="confirm-bulk-update-btn">Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sitemap Unit Edit Dialog */}
      <Dialog open={sitemapEditOpen} onOpenChange={setSitemapEditOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md" data-testid="sitemap-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl text-white">
              {selectedSitemapUnit ? `Edit Villa #${selectedSitemapUnit.villa_number}` : 'Add New Villa'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!selectedSitemapUnit && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-white/70">Villa # *</Label>
                    <Input
                      type="number"
                      value={sitemapFormData.villa_number}
                      onChange={(e) => setSitemapFormData({ ...sitemapFormData, villa_number: e.target.value })}
                      placeholder="e.g., 250"
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="sitemap-villa-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Type</Label>
                    <Select
                      value={sitemapFormData.villa_type}
                      onValueChange={(v) => {
                        const sqmDefault = v === 'A' ? 300 : v === 'C' ? 160 : 203;
                        setSitemapFormData({ ...sitemapFormData, villa_type: v, sqm: sqmDefault });
                      }}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="sitemap-villa-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="A">A (300m²)</SelectItem>
                        <SelectItem value="B">B (203m²)</SelectItem>
                        <SelectItem value="C">C (160m²)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Size (m²)</Label>
                    <Input
                      type="number"
                      value={sitemapFormData.sqm}
                      onChange={(e) => setSitemapFormData({ ...sitemapFormData, sqm: e.target.value })}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-white/70">Position X (%)</Label>
                    <Input
                      type="number"
                      min="0" max="100" step="0.1"
                      value={sitemapFormData.position_x}
                      onChange={(e) => setSitemapFormData({ ...sitemapFormData, position_x: e.target.value })}
                      placeholder="(optional)"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Position Y (%)</Label>
                    <Input
                      type="number"
                      min="0" max="100" step="0.1"
                      value={sitemapFormData.position_y}
                      onChange={(e) => setSitemapFormData({ ...sitemapFormData, position_y: e.target.value })}
                      placeholder="(optional)"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-white/30">Leave position blank and use Map Editor → Position Capture Mode to place it later.</p>
              </>
            )}
            <div className="space-y-2">
              <Label className="text-white/70">Status</Label>
              <Select value={sitemapFormData.status} onValueChange={(v) => setSitemapFormData({...sitemapFormData, status: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="company_buyer">Company Buyer</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Buyer Name</Label>
              <Input value={sitemapFormData.buyer_name} onChange={(e) => setSitemapFormData({...sitemapFormData, buyer_name: e.target.value})} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Phone</Label>
              <Input value={sitemapFormData.phone} onChange={(e) => setSitemapFormData({...sitemapFormData, phone: e.target.value})} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Price</Label>
              <Input value={sitemapFormData.price} onChange={(e) => setSitemapFormData({...sitemapFormData, price: e.target.value})} placeholder="e.g., $290,000" className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Contract Number</Label>
              <Input value={sitemapFormData.contract_number} onChange={(e) => setSitemapFormData({...sitemapFormData, contract_number: e.target.value})} className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSitemapEditOpen(false)} className="border-white/20 text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={handleSaveSitemapUnit} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#C5A059]" data-testid="save-sitemap-unit-btn">
              {saving ? 'Saving...' : selectedSitemapUnit ? 'Update Unit' : 'Create Unit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
